import "core-js/modules/es.function.name.js";
import React, { useRef } from 'react';
import useUniqueId from 'hooks/useUniqueId';
import { Checkbox } from 'components/CheckboxOption';
import { RadioButton } from 'components/RadioButtonOption';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Text from 'components/Text';
import OverflowTooltip from 'components/OverflowTooltip';
import SelectionIndicator from './SelectionIndicator';
import LabelBox from './LabelBox';

/**
 * A single selection picker has to name its radios, otherwise the browser treats
 * every row as its own group of one: a tab stop each, and no arrow key support
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function ListItem(_ref) {
  var isSingleItemPicker = _ref.isSingleItemPicker,
    id = _ref.id,
    name = _ref.name,
    label = _ref.label,
    subtitle = _ref.subtitle,
    icon = _ref.icon,
    inlineIcon = _ref.inlineIcon,
    isSelected = _ref.isSelected,
    isIndeterminate = _ref.isIndeterminate,
    isDisabled = _ref.isDisabled,
    onChange = _ref.onChange,
    style = _ref.style;
  var itemRef = useRef(null);
  var uniqueId = useUniqueId(String(id));
  var itemId = "list-item-".concat(uniqueId);
  var labelId = "list-item-label-".concat(uniqueId);
  var subtitleId = subtitle ? "list-item-subtitle-".concat(uniqueId) : undefined;
  var shouldAppearChecked = !isDisabled && (isSelected || isIndeterminate);
  function handleRowClick(e) {
    // We're handling changes on the wrapper element to make the whole
    // item picker row clickable.
    // Originally we were using a pseudo element inside of the <label>
    // that covered the row area, but this prevented using other
    // pointer events within the row, e.g. for tooltips
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      // We'll return the item and the new state the checkbox should be in.
      // Indeterminate checkboxes should always return to a checked state first.
      onChange(isIndeterminate || !isSelected);
    }
  }
  var iconStyle = isDisabled ? {
    opacity: 0.5
  } : {
    opacity: 1
  };
  return /*#__PURE__*/_jsxs(Flex, {
    position: "relative",
    borderBottom: "thin",
    height: 48,
    p: "xxs",
    pl: 0,
    style: style,
    onClick: handleRowClick,
    children: [shouldAppearChecked && /*#__PURE__*/_jsx(SelectionIndicator, {}), /*#__PURE__*/_jsx(Box, {
      position: "relative",
      display: "flex",
      z: "raised",
      ml: "l",
      mr: "m",
      children: isSingleItemPicker ? /*#__PURE__*/_jsx(RadioButton, {
        id: itemId,
        name: name,
        value: id,
        ref: itemRef,
        checked: isSelected,
        disabled: isDisabled,
        "aria-describedby": subtitleId,
        onChange: function onChange(e) {
          return e.preventDefault();
        }
      }) : /*#__PURE__*/_jsx(Checkbox, {
        id: itemId,
        ref: itemRef,
        checked: isSelected,
        disabled: isDisabled,
        size: "small",
        indeterminate: isIndeterminate,
        "aria-describedby": subtitleId,
        onChange: function onChange(e) {
          return e.preventDefault();
        }
      })
    }), icon && /*#__PURE__*/_jsx(Box, {
      mr: "s",
      style: iconStyle,
      children: icon
    }), inlineIcon && /*#__PURE__*/_jsx(Box, {
      mr: "xxs",
      style: iconStyle,
      children: inlineIcon
    }), /*#__PURE__*/_jsxs(Box, {
      grow: true,
      shrink: true,
      dimmed: isDisabled,
      basis: 0,
      lineHeight: 1.3,
      display: "flex",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(OverflowTooltip, {
        content: label,
        children: /*#__PURE__*/_jsx(LabelBox, {
          as: "label",
          display: "block",
          overflow: "ellipsis",
          position: "relative",
          id: labelId,
          htmlFor: itemId,
          isChecked: shouldAppearChecked,
          children: label
        })
      }), subtitle && /*#__PURE__*/_jsx(Text, {
        dimmed: true,
        size: "xs",
        overflow: "ellipsis",
        id: subtitleId,
        children: subtitle
      })]
    })]
  });
}
export default ListItem;